import { useCallback, useEffect, useMemo, useState } from "react";

export const WIZARD_STEPS = [
  "user",
  "delivery",
  "product",
  "shipping",
  "checkout",
  "status",
  "comments",
  "summary",
];

export function getStepOrder(deliveryType) {
  if (deliveryType === "correios") {
    return WIZARD_STEPS;
  }
  return WIZARD_STEPS.filter((s) => s !== "shipping");
}

export function isStepValid(step, state) {
  const { userData, deliveryData, productData, pendingComment, selectedShipping } =
    state;

  switch (step) {
    case "user":
      return Boolean(userData?._id);
    case "delivery": {
      const delivery = deliveryData?.delivery;
      if (!delivery) return false;
      if (delivery === "correios") return Boolean(deliveryData?.address);
      if (delivery === "presencial") return Boolean(deliveryData?.store);
      return false;
    }
    case "product": {
      const gradingType = productData?.gradingType;
      const numberOfCards = productData?.numberOfCards;
      const priority = productData?.priority;
      const declaredValue = productData?.declaredValue;
      return (
        Boolean(gradingType) &&
        Boolean(numberOfCards) &&
        numberOfCards !== "0" &&
        Boolean(priority) &&
        Number(declaredValue) >= 100
      );
    }
    case "shipping":
      return Boolean(selectedShipping?.service);
    case "checkout":
      return true;
    case "status":
      return Boolean(productData?.status);
    case "comments":
      return !pendingComment;
    case "summary":
      return true;
    default:
      return false;
  }
}

export function useCreateProductWizard({ initialUserData, initialStep } = {}) {
  const [step, setStep] = useState(
    initialStep || (initialUserData?._id ? "delivery" : "user")
  );
  const [userData, setUserData] = useState(initialUserData || {});
  const [deliveryData, setDeliveryData] = useState({});
  const [productData, setProductData] = useState({ status: "PENDING", comments: [] });
  const [selectedShipping, setSelectedShipping] = useState(null);
  const [pendingComment, setPendingComment] = useState("");

  const stepOrder = useMemo(
    () => getStepOrder(deliveryData?.delivery),
    [deliveryData?.delivery]
  );

  const stepIndex = stepOrder.indexOf(step);

  useEffect(() => {
    if (stepOrder.length && !stepOrder.includes(step)) {
      setStep(stepOrder.includes("delivery") ? "delivery" : stepOrder[0]);
    }
  }, [step, stepOrder]);

  const validationState = useMemo(
    () => ({ userData, deliveryData, productData, pendingComment, selectedShipping }),
    [userData, deliveryData, productData, pendingComment, selectedShipping]
  );

  const canNext = isStepValid(step, validationState);
  const canPrev = stepIndex > 0;

  const next = useCallback(() => {
    if (!isStepValid(step, validationState)) return;
    const nextStep = stepOrder[stepIndex + 1];
    if (nextStep) setStep(nextStep);
  }, [step, stepIndex, stepOrder, validationState]);

  const prev = useCallback(() => {
    const prevStep = stepOrder[stepIndex - 1];
    if (prevStep) setStep(prevStep);
  }, [stepIndex, stepOrder]);

  const goToStep = useCallback(
    (target) => {
      if (stepOrder.includes(target)) setStep(target);
    },
    [stepOrder]
  );

  return {
    step,
    stepIndex,
    stepOrder,
    userData,
    setUserData,
    deliveryData,
    setDeliveryData,
    productData,
    setProductData,
    selectedShipping,
    setSelectedShipping,
    pendingComment,
    setPendingComment,
    canNext,
    canPrev,
    next,
    prev,
    goToStep,
  };
}
